import { CanvasTextMetrics, Container, FillGradient, Graphics, Sprite, Text, TextStyle, type TextStyleOptions } from 'pixi.js';
import { AudioBus } from '../../audio/AudioBus';
import { glowTexture } from '../road/glow';
import { CoinBurst } from './CoinBurst';
import { Confetti } from './Confetti';
import { beamTexture, raysTexture } from './titleArt';
import { pickTitleLook, type TitleColourway, type TitleKind, type TitleLook } from './titleLooks';
import { animate, COLOURS, DISPLAY_FONT, ease, prefersReducedMotion, uiStyle } from './ui';

export interface TitleCardOptions {
  /** A line under the title. */
  sub?: string;
  /** The sound it opens with. */
  sting?: string;
  /** How long it holds once in (a tap cuts it short). */
  holdMs?: number;
  /** What it's for: picks the look, with a little chance in it. With no
   *  kind it keeps the original look (it slams in on the band, in gold). */
  kind?: TitleKind;
  /** Any part of the look to use instead of the one picked. */
  look?: Partial<TitleLook>;
}

interface Palette {
  /** The band, dark at the bottom and lighter at the top. */
  band: number;
  bandLight: number;
  /** The band's edges, the ribbon's trim and the stamp's frame. */
  edge: number;
  /** The title's fill, top to bottom. */
  top: number;
  bottom: number;
  stroke: number;
  sub: number;
  /** Rays, the spotlight's pool and the burst's ring. */
  glow: number;
  beam: number;
  sparks: readonly [number, number];
}

const PALETTES: Readonly<Record<TitleColourway, Palette>> = {
  gold: {
    band: 0x120a02, bandLight: 0x4d3409, edge: COLOURS.brassLight, top: 0xfff7cf, bottom: 0xffc63a,
    stroke: COLOURS.ink, sub: COLOURS.cream, glow: 0xffcf5a, beam: 0xfff0c8, sparks: [0xffd36b, 0xffffff],
  },
  wood: {
    band: COLOURS.woodDark, bandLight: 0x8a5524, edge: COLOURS.brass, top: 0xfff3d6, bottom: 0xffd27a,
    stroke: COLOURS.ink, sub: COLOURS.cream, glow: 0xf2b66b, beam: 0xffe9c4, sparks: [0xffc27a, 0xfff1c0],
  },
  cool: {
    band: 0x06131f, bandLight: 0x1a4566, edge: 0x9fdcf5, top: 0xffffff, bottom: 0x9fdcff,
    stroke: 0x05182a, sub: 0xd8f1ff, glow: 0x7cc8f0, beam: 0xdff4ff, sparks: [0xbfe9ff, 0xffffff],
  },
  ruby: {
    band: 0x260505, bandLight: 0x7a1414, edge: 0xffcf6b, top: 0xfff7cf, bottom: 0xffc63a,
    stroke: 0x260404, sub: COLOURS.cream, glow: 0xff9a3d, beam: 0xffe2c0, sparks: [0xffb347, 0xfff1c0],
  },
};

const gradients = new Map<string, FillGradient>();

/** A top-to-bottom gradient in the box it fills, made once per pair. */
function vertical(top: number, bottom: number, mid?: number): FillGradient {
  const key = `${top}:${bottom}:${mid ?? ''}`;
  let g = gradients.get(key);
  if (!g) {
    const stops = mid === undefined
      ? [{ offset: 0, color: top }, { offset: 1, color: bottom }]
      : [{ offset: 0, color: top }, { offset: 0.45, color: mid }, { offset: 1, color: bottom }];
    g = new FillGradient({ type: 'linear', start: { x: 0, y: 0 }, end: { x: 0, y: 1 }, colorStops: stops, textureSpace: 'local' });
    gradients.set(key, g);
  }
  return g;
}

function titleStyle(size: number, p: Palette): TextStyleOptions {
  return {
    fontFamily: DISPLAY_FONT,
    fontSize: size,
    fill: vertical(p.top, p.bottom),
    letterSpacing: Math.round(size * 0.05),
    stroke: { color: p.stroke, width: Math.max(2, Math.round(size * 0.14)), join: 'round' },
    dropShadow: { color: 0x000000, alpha: 0.55, blur: 3, distance: Math.max(2, Math.round(size * 0.05)), angle: Math.PI / 2 },
  };
}

const outBounce = (t: number): number => {
  const n = 7.5625;
  const d = 2.75;
  if (t < 1 / d) return n * t * t;
  if (t < 2 / d) return n * (t -= 1.5 / d) * t + 0.75;
  if (t < 2.5 / d) return n * (t -= 2.25 / d) * t + 0.9375;
  return n * (t -= 2.625 / d) * t + 0.984375;
};

/** A strong overshoot, for the burst. */
const outBackStrong = (t: number): number => {
  const c1 = 2.6;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};

/**
 * The TV moment before a run or a game starts, or when something is won or
 * completed (producer 2026-10-14: "this is TV"): big type over the game,
 * a line under it and a sting. Each card has a look (see
 * {@link pickTitleLook}): an entrance (a slam, a sweep and shine, a stamp,
 * a burst, letters dropping in, a ribbon unfurling), a backdrop (the band,
 * rays, a spotlight, confetti or coins) and a colourway, picked by what the
 * card is for. Add one to the stage above the game and call {@link play};
 * it resolves when the card has gone. Tapping skips the hold. With reduced
 * motion the card simply appears, holds and goes.
 */
export class TitleCard extends Container {
  private readonly dim = new Graphics();
  private readonly rays: Sprite;
  private readonly beams: [Sprite, Sprite];
  private readonly pool: Sprite;
  private readonly band = new Graphics();
  private readonly ring = new Graphics();
  private readonly flash: Sprite;
  private readonly sparks: Sprite[] = [];
  private readonly puffs: Sprite[] = [];
  private readonly confetti = new Confetti();
  private readonly coins = new CoinBurst();
  /** The title and the stamp's frame, moved and turned together. */
  private readonly titleBox = new Container();
  private readonly frame = new Graphics();
  private readonly title: Text;
  private readonly letters = new Container();
  private readonly streak: Sprite;
  private readonly sub: Text;
  private viewW = 1;
  private viewH = 1;
  private skip: (() => void) | null = null;
  private playing = 0;
  private readonly lastByKind = new Map<TitleKind, TitleLook>();
  /** The card on screen (or last shown), for tests and the preview. */
  shown: { text: string; kind: TitleKind | null; look: TitleLook } | null = null;

  constructor() {
    super();
    this.rays = new Sprite(raysTexture());
    this.rays.anchor.set(0.5);
    this.rays.blendMode = 'add';
    this.beams = [new Sprite(beamTexture()), new Sprite(beamTexture())];
    this.beams.forEach((b) => {
      b.anchor.set(0.5, 0);
      b.blendMode = 'add';
    });
    this.pool = new Sprite(glowTexture());
    this.pool.anchor.set(0.5);
    this.pool.blendMode = 'add';
    this.flash = new Sprite(glowTexture());
    this.flash.anchor.set(0.5);
    this.flash.blendMode = 'add';
    this.title = new Text({ text: '', style: titleStyle(64, PALETTES.gold) });
    this.title.anchor.set(0.5);
    this.sub = new Text({ text: '', style: uiStyle(18, COLOURS.cream) });
    this.sub.anchor.set(0.5, 0);
    this.streak = new Sprite(glowTexture());
    this.streak.anchor.set(0.5);
    this.streak.blendMode = 'add';
    this.streak.tint = 0xfff1c0;
    for (let i = 0; i < 14; i++) {
      const s = new Sprite(glowTexture());
      s.anchor.set(0.5);
      s.blendMode = 'add';
      this.sparks.push(s);
    }
    for (let i = 0; i < 12; i++) {
      const s = new Sprite(glowTexture());
      s.anchor.set(0.5);
      s.tint = 0xfff1d6;
      this.puffs.push(s);
    }
    this.titleBox.addChild(this.frame, this.title, this.letters);
    this.addChild(
      this.dim,
      this.rays,
      ...this.beams,
      this.pool,
      this.band,
      this.ring,
      this.flash,
      ...this.sparks,
      this.confetti,
      this.coins,
      ...this.puffs,
      this.titleBox,
      this.streak,
      this.sub,
    );
    this.eventMode = 'static';
    this.on('pointertap', () => this.skip?.());
    this.visible = false;
  }

  layout(viewWidth: number, viewHeight: number): void {
    this.viewW = viewWidth;
    this.viewH = viewHeight;
    this.hitArea = { contains: (x: number, y: number) => x >= 0 && y >= 0 && x <= viewWidth && y <= viewHeight };
  }

  /** Show `text` (and an optional line under it) and play `sting`. */
  async play(text: string, options: TitleCardOptions = {}): Promise<void> {
    const kind = options.kind ?? null;
    const look = pickTitleLook(options.kind, { last: kind ? this.lastByKind.get(kind) : null, fixed: options.look });
    if (kind) this.lastByKind.set(kind, look);
    this.shown = { text, kind, look };
    const id = ++this.playing;
    const p = PALETTES[look.colour];
    const reduced = prefersReducedMotion();
    const { viewW: w, viewH: h } = this;
    const cx = w / 2;
    const cy = Math.round(h * 0.42);

    // The title: as big as the screen allows, shrunk to fit a narrow one.
    const framed = look.entrance === 'stamp';
    let size = Math.round(Math.max(40, Math.min(84, w * 0.13)));
    this.title.style = titleStyle(size, p);
    this.title.text = text;
    // A stamp's frame and a ribbon's tails need room of their own.
    const ribbon = look.entrance === 'ribbon';
    const room = ribbon ? w - 8 - size * 2.5 : w - 32 - (framed ? size * 0.7 : 0);
    if (this.title.width > room) {
      size = Math.max(24, Math.floor((size * room) / this.title.width));
      this.title.style = titleStyle(size, p);
    }
    const titleW = this.title.width;
    this.resetPieces();
    this.titleBox.position.set(cx, cy);

    // Letters, for the drop: each where it sits in the whole title. The
    // whole title stays in the scene (tests and the like read it), unseen.
    const dropping = look.entrance === 'drop';
    this.title.renderable = !dropping;
    if (dropping) this.buildLetters(text, size, p);

    // The stamp's inked frame.
    const frameW = titleW + size * 0.6;
    const frameH = size * 1.3;
    if (framed) {
      const r = size * 0.18;
      const line = Math.max(3, Math.round(size * 0.07));
      this.frame
        .roundRect(-frameW / 2, -frameH / 2, frameW, frameH, r)
        .stroke({ color: p.stroke, width: line + Math.max(3, size * 0.08), join: 'round' })
        .roundRect(-frameW / 2, -frameH / 2, frameW, frameH, r)
        .stroke({ color: p.bottom, width: line, join: 'round' })
        .roundRect(-frameW / 2 + line * 1.8, -frameH / 2 + line * 1.8, frameW - line * 3.6, frameH - line * 3.6, r * 0.7)
        .stroke({ color: p.edge, width: Math.max(1, line * 0.4), alpha: 0.8 });
    }

    // The ribbon holds the title alone; the band holds the line under it too.
    const ribbonH = size * 1.45;
    const below = ribbon ? ribbonH / 2 + 12 : framed ? frameH / 2 + 10 : size * 0.6;
    this.sub.style = {
      ...uiStyle(18, p.sub),
      stroke: { color: p.stroke, width: 3, join: 'round' },
      align: 'center',
      wordWrap: true,
      wordWrapWidth: Math.max(160, w - 48),
    };
    this.sub.text = options.sub ?? '';
    const subTop = cy + below;
    this.sub.position.set(cx, subTop);
    const bandTop = cy - Math.max(size * 1.05, framed ? frameH / 2 + 12 : 0) - (options.sub ? 8 : 0);
    const bandBottom = options.sub ? subTop + this.sub.height + 14 : cy + Math.max(size * 1.05, framed ? frameH / 2 + 12 : 0);

    const spotlit = look.backdrop === 'spotlight';
    const banded = !ribbon && !spotlit;
    const dimAlpha = spotlit ? 0.62 : 0.38;
    const drawDim = (alpha: number) => this.dim.clear().rect(0, 0, w, h).fill({ color: 0x000000, alpha: dimAlpha * alpha });
    const drawBand = (reveal: number, alpha: number) => {
      this.band.clear();
      if (!banded) return;
      const bw = w * reveal;
      this.band
        .rect(0, bandTop, bw, bandBottom - bandTop)
        .fill({ fill: vertical(p.bandLight, p.band, p.band), alpha: (look.colour === 'wood' ? 0.95 : 0.88) * alpha });
      if (look.colour === 'wood') this.grain(0, bandTop, bw, bandBottom - bandTop, alpha);
      this.band
        .rect(0, bandTop, bw, 2)
        .fill({ color: p.edge, alpha })
        .rect(0, bandBottom - 2, bw, 2)
        .fill({ color: p.edge, alpha })
        .rect(0, bandTop + 5, bw, 1)
        .fill({ color: p.edge, alpha: 0.35 * alpha })
        .rect(0, bandBottom - 6, bw, 1)
        .fill({ color: p.edge, alpha: 0.35 * alpha });
    };
    // The ribbon and its tails stay on screen.
    const ribbonW = Math.min(w - 8 - size * 1.9, titleW + size * 1.2);
    const drawRibbon = (reveal: number, tails: number) => this.drawRibbon(cx, cy, ribbonW * reveal, ribbonH, size, tails, p);

    this.visible = true;
    this.alpha = 1;
    this.position.set(0, 0);
    this.sub.alpha = 0;
    if (options.sting) AudioBus.shared.play(options.sting, { volume: 0.85 });

    // What keeps moving while the card is up: the rays turn, the
    // spotlights sway.
    const rayScale = (Math.max(w, h) * 1.15) / 512;
    this.rays.position.set(cx, cy);
    this.rays.tint = p.glow;
    let spot = { x: -w * 0.4, sway: 0 };
    const drawSpot = (alpha: number) => this.drawSpotlight(cx + spot.x + spot.sway, cy, titleW, size, alpha, p);
    if (!reduced) {
      let last = performance.now();
      const tick = (now: number) => {
        if (this.destroyed || this.playing !== id || !this.visible) return;
        const dt = Math.min(3, (now - last) / 16.7);
        last = now;
        if (look.backdrop === 'rays') this.rays.rotation += 0.0035 * dt;
        if (spotlit && spot.x === 0) {
          spot.sway = Math.sin(now / 520) * size * 0.08;
          drawSpot(1);
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }

    // 1. The stage: the screen dims and the backdrop comes in.
    const stage = animate(240, (t) => {
      const e = ease.outCubic(t);
      drawDim(e);
      drawBand(e, 1);
      if (look.backdrop === 'rays') {
        this.rays.alpha = 0.42 * e;
        this.rays.scale.set(rayScale * (0.4 + 0.6 * e));
      }
    });
    // Two spotlights swing in from either side and meet on the title.
    const swing = spotlit
      ? animate(380, (t) => {
          spot = { x: -w * 0.4 * (1 - ease.outCubic(t)), sway: 0 };
          drawSpot(Math.min(1, t * 2));
        })
      : null;
    await Promise.all([stage, swing]);
    spot = { x: 0, sway: 0 };

    // 2. The title comes in, its own way.
    const impact = () => this.impact(look, cx, cy, size, titleW, frameW, frameH, reduced);
    switch (look.entrance) {
      case 'slam':
        await this.slam(size, w, cx, cy, impact);
        break;
      case 'sweep':
        await this.sweepIn(w, cx, titleW, impact);
        break;
      case 'stamp':
        await this.stampIn(impact);
        break;
      case 'burst':
        await this.burstIn(size, w, cx, cy, impact);
        break;
      case 'drop':
        await this.dropIn(h, impact);
        break;
      case 'ribbon':
        await animate(360, (t) => drawRibbon(ease.outCubic(t), 0));
        await animate(160, (t) => {
          drawRibbon(1, ease.outCubic(t));
          this.titleBox.alpha = t;
          this.titleBox.scale.set(0.82 + 0.18 * ease.outBack(t));
        });
        impact();
        break;
    }

    // 3. The line under it rises in; a shine runs over a slam or a sweep.
    const shine = look.entrance === 'slam' || look.entrance === 'sweep';
    void animate(520, (t) => {
      if (shine) {
        this.streak.alpha = Math.sin(t * Math.PI) * 0.9;
        this.streak.width = size * 1.4;
        this.streak.height = size * 1.6;
        this.streak.position.set(cx - titleW / 2 + titleW * t, cy);
      }
      this.sub.alpha = t;
      this.sub.y = subTop + 10 * (1 - ease.outCubic(t));
    });
    await this.hold(options.holdMs ?? 1100);

    // 4. Off it goes: a sweep carries on to the right, the rest lift away.
    await animate(260, (t) => {
      const e = ease.inCubic(t);
      if (look.entrance === 'sweep') {
        this.titleBox.x = cx + w * 0.6 * e;
        this.titleBox.skew.x = -0.3 * e;
      } else {
        this.titleBox.scale.set(1 + 0.25 * e);
      }
      this.alpha = 1 - t;
    });
    if (this.playing !== id) return;
    this.visible = false;
    this.alpha = 1;
    this.position.set(0, 0);
    this.confetti.clear();
    this.coins.clear();
    this.letters.removeChildren().forEach((l) => l.destroy());
  }

  /** Put every piece back at rest, out of sight. */
  private resetPieces(): void {
    this.dim.clear();
    this.band.clear();
    this.ring.clear();
    this.frame.clear();
    this.rays.alpha = 0;
    this.rays.rotation = Math.random() * Math.PI;
    this.beams.forEach((b) => (b.alpha = 0));
    this.pool.alpha = 0;
    this.flash.alpha = 0;
    this.streak.alpha = 0;
    this.sparks.forEach((s) => (s.alpha = 0));
    this.puffs.forEach((s) => (s.alpha = 0));
    this.confetti.clear();
    this.coins.clear();
    this.letters.removeChildren().forEach((l) => l.destroy());
    this.titleBox.alpha = 0;
    this.titleBox.scale.set(1);
    this.titleBox.rotation = 0;
    this.titleBox.skew.set(0, 0);
    this.letters.position.set(0, 0);
  }

  /** One Text per letter, centred where it sits in the whole title. */
  private buildLetters(text: string, size: number, p: Palette): void {
    const style = new TextStyle(titleStyle(size, p));
    const widthOf = (s: string) => (s ? CanvasTextMetrics.measureText(s, style).width : 0);
    const whole = widthOf(text);
    const chars = [...text];
    let prefix = '';
    chars.forEach((ch) => {
      prefix += ch;
      if (ch.trim() === '') return;
      const letter = new Text({ text: ch, style });
      letter.anchor.set(0.5);
      letter.x = widthOf(prefix) - widthOf(ch) / 2 - whole / 2;
      this.letters.addChild(letter);
    });
  }

  /** The ribbon banner: a band `width` wide (it grows from the middle as it
   *  unfurls), then its folded tails slide out (`tails` 0 to 1). */
  private drawRibbon(cx: number, cy: number, width: number, height: number, size: number, tails: number, p: Palette): void {
    const g = this.band.clear();
    if (width <= 1) return;
    const half = width / 2;
    const top = cy - height / 2;
    if (tails > 0) {
      const tailW = size * 0.95 * tails;
      const drop = height * 0.28;
      const th = height * 0.86;
      const notch = size * 0.32 * tails;
      for (const side of [-1, 1]) {
        const inner = cx + side * (half - size * 0.25);
        const outer = cx + side * (half + tailW);
        const ty = top + drop;
        // The tail, its end cut in a V.
        g.poly([inner, ty, outer, ty, outer - side * notch, ty + th / 2, outer, ty + th, inner, ty + th])
          .fill({ color: p.band })
          .stroke({ color: p.edge, width: 2, alpha: 0.85 });
        // The fold where it tucks behind the band.
        g.poly([cx + side * half, top + height, cx + side * (half - size * 0.25), ty + th, cx + side * (half - size * 0.25), top + height])
          .fill({ color: 0x000000, alpha: 0.55 });
      }
    }
    g.rect(cx - half, top, width, height).fill({ fill: vertical(p.bandLight, p.band, p.band), alpha: 1 });
    if (p === PALETTES.wood) this.grain(cx - half, top, width, height, 1);
    g
      .rect(cx - half, top + 3, width, 2)
      .fill({ color: p.edge })
      .rect(cx - half, top + height - 5, width, 2)
      .fill({ color: p.edge })
      .rect(cx - half, top, width, 1)
      .fill({ color: 0x000000, alpha: 0.4 });
  }

  /** Wood grain over a band: thin dark streaks along it, the same each time. */
  private grain(x: number, y: number, width: number, height: number, alpha: number): void {
    if (width <= 1) return;
    for (let i = 0; i < 7; i++) {
      const gy = y + height * (0.12 + ((i * 0.37) % 1) * 0.76);
      const gx = x + width * ((i * 0.23) % 0.3);
      this.band.rect(gx, gy, width - (gx - x) - width * ((i * 0.41) % 0.25), i % 3 === 0 ? 2 : 1).fill({ color: 0x1f1005, alpha: 0.28 * alpha });
    }
  }

  /** The two crossing spotlights and their pool, aimed at `(x, cy)`. */
  private drawSpotlight(x: number, cy: number, titleW: number, size: number, alpha: number, p: Palette): void {
    const { viewW: w } = this;
    const sources = [w * 0.2, w * 0.8];
    this.beams.forEach((beam, i) => {
      const sx = sources[i]!;
      const sy = -size * 1.2;
      const tx = x + (i === 0 ? -1 : 1) * titleW * 0.08;
      const ty = cy + size * 0.5;
      const dx = tx - sx;
      const dy = ty - sy;
      const length = Math.hypot(dx, dy);
      beam.position.set(sx, sy);
      beam.rotation = -Math.atan2(dx, dy);
      beam.height = length;
      beam.width = titleW * 0.55 + size * 0.8;
      beam.tint = p.beam;
      beam.alpha = 0.26 * alpha;
    });
    this.pool.position.set(x, cy + size * 0.1);
    this.pool.width = titleW * 1.5 + size;
    this.pool.height = size * 2.6;
    this.pool.tint = p.glow;
    this.pool.alpha = 0.5 * alpha;
  }

  /** The moment the title lands: whatever the backdrop and entrance add. */
  private impact(look: TitleLook, cx: number, cy: number, size: number, titleW: number, frameW: number, frameH: number, reduced: boolean): void {
    if (reduced) return;
    const p = PALETTES[look.colour];
    if (look.backdrop === 'confetti') {
      void this.confetti.burst(cx - titleW * 0.3, cy + size * 0.3, { count: 45, spread: 300 });
      void this.confetti.burst(cx + titleW * 0.3, cy + size * 0.3, { count: 45, spread: 300 });
    }
    if (look.backdrop === 'coins') {
      void this.coins.burst(cx, cy + size * 0.2, { count: 34, spread: Math.min(420, this.viewW * 0.8), size: Math.max(22, size * 0.42) });
    }
    if (look.entrance === 'stamp') {
      // A puff of dust round the frame, and a jolt.
      this.puffs.forEach((s, i) => {
        const along = (i / (this.puffs.length - 1) - 0.5) * frameW;
        const bottom = i % 2 === 0;
        s.position.set(cx + along, cy + (bottom ? frameH / 2 : -frameH / 2));
        s.scale.set(0.5);
        s.alpha = 0;
      });
      void animate(420, (t) => {
        this.puffs.forEach((s, i) => {
          const dir = i % 2 === 0 ? 1 : -1;
          const spreadX = ((i / (this.puffs.length - 1)) - 0.5) * 2;
          s.alpha = 0.55 * (1 - t);
          s.scale.set(0.5 + 1.1 * ease.outCubic(t));
          s.x += spreadX * 1.2;
          s.y += dir * 0.6;
        });
      });
      this.shake(4, 200);
    }
    if (look.entrance === 'burst') {
      this.ring.tint = p.edge;
      void animate(520, (t) => {
        const e = ease.outCubic(t);
        this.ring
          .clear()
          .circle(cx, cy, size * 0.6 + Math.max(this.viewW, this.viewH) * 0.6 * e)
          .stroke({ color: 0xffffff, width: Math.max(1, 10 * (1 - t)), alpha: 0.9 * (1 - t) });
      });
    }
    if (look.entrance === 'slam') this.shake(9, 300);
  }

  /** From big, down hard with a little bounce; sparks burst from behind. */
  private async slam(size: number, w: number, cx: number, cy: number, impact: () => void): Promise<void> {
    const p = PALETTES[this.shown!.look.colour];
    const angles = this.sparks.map((_, i) => (i / this.sparks.length) * Math.PI * 2 + Math.random() * 0.4);
    this.sparks.forEach((s, i) => (s.tint = p.sparks[i % 2]!));
    let landed = false;
    await animate(420, (t) => {
      const e = ease.outBack(t);
      this.titleBox.alpha = Math.min(1, t * 2.5);
      this.titleBox.scale.set(2.2 - 1.2 * e);
      if (!landed && t >= 0.5) {
        landed = true;
        impact();
      }
      this.sparks.forEach((s, i) => {
        const r = (size * 0.8 + w * 0.25 * ease.outCubic(t)) * (0.6 + (i % 3) * 0.2);
        s.position.set(cx + Math.cos(angles[i]!) * r, cy + Math.sin(angles[i]!) * r * 0.45);
        s.alpha = t < 0.3 ? t / 0.3 : 1 - (t - 0.3) / 0.7;
        s.scale.set(0.25 + 0.2 * (i % 3));
      });
    });
    if (!landed) impact();
  }

  /** In from the left at speed, leaning forward, straightening as it stops. */
  private async sweepIn(w: number, cx: number, titleW: number, impact: () => void): Promise<void> {
    const from = -titleW / 2 - w * 0.1;
    await animate(380, (t) => {
      const e = ease.outCubic(t);
      this.titleBox.alpha = Math.min(1, t * 3);
      this.titleBox.x = from + (cx - from) * e;
      this.titleBox.skew.x = -0.38 * (1 - e);
    });
    // A little overshoot back the other way, like a car pulling up.
    await animate(140, (t) => {
      this.titleBox.skew.x = 0.08 * Math.sin(t * Math.PI);
    });
    impact();
  }

  /** Down from above the screen, hard, askew; squashes as it lands. */
  private async stampIn(impact: () => void): Promise<void> {
    const tilt = (Math.random() < 0.5 ? -1 : 1) * 0.06;
    await animate(240, (t) => {
      const e = ease.inCubic(t);
      this.titleBox.alpha = Math.min(1, t * 4);
      this.titleBox.scale.set(2 - e);
      this.titleBox.rotation = tilt * 2.4 - tilt * 1.4 * e;
    });
    impact();
    await animate(170, (t) => {
      const squash = Math.sin(t * Math.PI) * 0.1;
      this.titleBox.scale.set(1 + squash, 1 - squash);
    });
  }

  /** Out of a flash: from nothing it bursts past its size and settles, a
   *  ring of light racing out behind it. */
  private async burstIn(size: number, w: number, cx: number, cy: number, impact: () => void): Promise<void> {
    const p = PALETTES[this.shown!.look.colour];
    this.flash.position.set(cx, cy);
    this.flash.tint = p.top;
    impact();
    const angles = this.sparks.map((_, i) => (i / this.sparks.length) * Math.PI * 2);
    this.sparks.forEach((s, i) => (s.tint = p.sparks[i % 2]!));
    await animate(480, (t) => {
      this.titleBox.alpha = Math.min(1, t * 4);
      this.titleBox.scale.set(Math.max(0.05, outBackStrong(t)));
      this.flash.alpha = t < 0.08 ? t / 0.08 : Math.max(0, 1 - (t - 0.08) / 0.32);
      this.flash.width = size * (1.5 + 7 * ease.outCubic(t));
      this.flash.height = size * (1 + 3 * ease.outCubic(t));
      this.sparks.forEach((s, i) => {
        const r = size * 0.4 + w * 0.42 * ease.outCubic(t) * (0.7 + (i % 3) * 0.15);
        s.position.set(cx + Math.cos(angles[i]!) * r, cy + Math.sin(angles[i]!) * r * 0.55);
        s.alpha = 1 - t;
        s.scale.set(0.3 + 0.15 * (i % 3));
      });
    });
  }

  /** The letters drop in one after another from above and bounce. */
  private async dropIn(h: number, impact: () => void): Promise<void> {
    const letters = this.letters.children;
    const n = letters.length;
    const stagger = n > 1 ? Math.min(60, 380 / (n - 1)) : 0;
    const fall = 460;
    const start = -h * 0.55;
    const spins = letters.map(() => (Math.random() - 0.5) * 0.6);
    this.titleBox.alpha = 1;
    letters.forEach((l) => (l.y = start));
    let landed = false;
    await animate(fall + stagger * Math.max(0, n - 1), (t) => {
      const now = t * (fall + stagger * Math.max(0, n - 1));
      letters.forEach((l, i) => {
        const lt = Math.max(0, Math.min(1, (now - i * stagger) / fall));
        l.y = start * (1 - outBounce(lt));
        l.rotation = spins[i]! * (1 - Math.min(1, lt * 1.6));
        l.alpha = lt > 0 ? 1 : 0;
      });
      if (!landed && now >= fall * 0.36) {
        landed = true;
        impact();
      }
    });
    letters.forEach((l) => {
      l.y = 0;
      l.rotation = 0;
      l.alpha = 1;
    });
    if (!landed) impact();
  }

  /** Jolt the whole card by up to `px`, dying away over `ms`. */
  private shake(px: number, ms: number): void {
    void animate(ms, (t) => {
      const k = px * (1 - t) * (1 - t);
      this.position.set((Math.random() - 0.5) * 2 * k, (Math.random() - 0.5) * 2 * k);
      if (t >= 1) this.position.set(0, 0);
    });
  }

  private hold(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const done = () => {
        clearTimeout(timer);
        this.skip = null;
        resolve();
      };
      const timer = setTimeout(done, ms);
      this.skip = done;
    });
  }
}
